import { AxiosResponse } from 'axios';
import api from './client';

/**
 * systemStatusService — read-only monitoring data for the admin System Status page.
 * Admin-gated backend routes (registered manually in app.go, like /order-export):
 *   GET /system-status/overview   app runtime + PDF queue + dependency health + cheap counts
 *   GET /system-status/storage    file_metadata aggregates + real disk usage + report_output
 *   GET /system-status/database   pg_database_size + top tables + connection-pool stats
 *
 * Note the two storage numbers legitimately differ: files.physicalBytes is what the app
 * tracks in file_metadata, while uploadsDisk is the filesystem ground truth (which also
 * counts orphans). The page labels them apart.
 */

export interface RoleCount {
  role: string;
  count: number;
}

export interface OverviewResponse {
  app: {
    version: string;
    companyCode: string;
    uptimeSeconds: number;
    goVersion: string;
    goroutines: number;
    memAllocBytes: number;
    memSysBytes: number;
    numGC: number;
  };
  pdfQueue: { depth: number; capacity: number };
  dependencies: {
    database: { ok: boolean; latencyMs: number };
    headlessChrome: { ok: boolean; detail: string };
  };
  counts: {
    users: number;
    usersByRole: RoleCount[];
    notifications7d: number;
    changeLogs: number;
  };
}

export interface EntityStorage {
  entityType: string;
  fileCount: number;
  physicalBytes: number;
}

export interface MonthGrowth {
  month: string; // "2006-01"
  fileCount: number;
  physicalBytes: number;
}

// DiskUsage / DirSize carry an `available` flag: disk stats are unsupported off linux/darwin,
// and report_output may not exist yet in dev — in both cases the rest of the panel still renders.
export interface DiskUsage {
  available: boolean;
  totalBytes?: number;
  freeBytes?: number;
  usedBytes?: number;
}

export interface DirSize {
  available: boolean;
  bytes?: number;
  entries?: number;
  truncated?: boolean;
}

export interface StorageResponse {
  files: {
    totalFiles: number;
    linkedFiles: number;
    physicalBytes: number;
    byEntityType: EntityStorage[];
    monthlyGrowth: MonthGrowth[];
  };
  uploadsDisk: DiskUsage;
  reportOutput: DirSize;
}

export interface TableStat {
  name: string;
  rows: number;
  totalBytes: number;
}

export interface DatabaseResponse {
  sizeAvailable: boolean;
  databaseSizeBytes?: number;
  tablesAvailable: boolean;
  tables?: TableStat[];
  pool: {
    maxConns: number;
    totalConns: number;
    idleConns: number;
    acquiredConns: number;
    acquireCount: number;
  };
}

export const systemStatusService = {
  getOverview: async (): Promise<AxiosResponse<OverviewResponse>> => {
    return api.get('/system-status/overview');
  },
  getStorage: async (): Promise<AxiosResponse<StorageResponse>> => {
    return api.get('/system-status/storage');
  },
  getDatabase: async (): Promise<AxiosResponse<DatabaseResponse>> => {
    return api.get('/system-status/database');
  },
};
